import { createContext, useCallback, useContext, useEffect, useState } from 'react'
import { API_BASE_URL } from '@/lib/api'
import { clearExternalLinkConfirmations } from '@/components/stories/OffSiteLinkConfirmModal'
import {
  endBriefingContextSession,
  notifyBriefingContextAuthSubject,
} from '@/lib/briefing-context-bootstrap'

export interface AuthUser {
  sub: string
  email: string
  name: string
  isAdmin: boolean
  /**
   * KAN-32 (R12 / O3) — the stored session-replay objection. Rides on `/me`
   * because a control honoured 300 ms after boot cannot gate recording that
   * starts at boot. OPTIONAL on this type deliberately: an older cached
   * response has no field, and `shouldRecord` requires `optedOut === false`,
   * so "absent" reads as unresolved and DENIES.
   */
  replayOptOut?: boolean
  /**
   * KAN-519 — account-level analytics objection / future-capture suppression.
   * Optional deliberately: an older/stale `/me` shape is unresolved for capture
   * and therefore must fail closed in the analytics bridge.
   */
  analyticsSuppressed?: boolean
  /**
   * KAN-32 A.10 (R10 / O8) — the active replay investigation, or null when
   * none is open (the default posture). Expiry was already evaluated
   * server-side; under multiplicity `routes` is the UNION across live
   * investigations and `expiresAt` the EARLIEST live expiry (KAN-594). The
   * client clock never EXTENDS recording: `shouldRecord` 3b uses `expiresAt`
   * as a deny-only stop, and `useReplayRecording` uses it to schedule the
   * boundary session refresh that re-fetches the surviving union.
   */
  replayInvestigation?: { routes: string[]; expiresAt: string } | null
}

interface AuthContextValue {
  user: AuthUser | null
  isLoading: boolean
  isAdmin: boolean
  logout: () => Promise<void>
  refreshUser: () => Promise<void>
}

// Exported so tests can supply a REAL context value rather than mocking
// `useAuth`. A mocked hook hides the one defect that matters here: a consumer
// mounted OUTSIDE <AuthProvider>, where useAuth() returns { user: null }
// forever and every test still passes. See analytics-replay.test.tsx.
export const AuthContext = createContext<AuthContextValue>({
  user: null,
  isLoading: true,
  isAdmin: false,
  logout: async () => {},
  refreshUser: async () => {},
})

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  /**
   * KAN-32 (R12 / O3 / Art. 21): `/me` carries `replayOptOut`, and it was
   * fetched exactly once on mount. Toggling the objection in Settings
   * invalidates ['user-preferences', userId] — a different query — so this
   * context stayed stale and recording continued until a page reload. An
   * objection honoured only after reload is not honoured.
   */
  const refreshUser = useCallback(async () => {
    let next: AuthUser | null
    try {
      const res = await fetch(`${API_BASE_URL}/auth/me`, { credentials: 'include' })
      next = res.ok ? ((await res.json()) as AuthUser) : null
    } catch {
      next = null
    }
    setUser(next)
    // KAN-661 R32/R38/R39. Every `/me` resolution is where a change of person
    // becomes knowable — a different subject, or a session that expired out from
    // under the tab. The bridge cancels in-flight contextual requests BEFORE the
    // caches holding their results are dropped; the reverse order leaves a
    // window in which a response already on the wire repopulates the new
    // subject's cache with the previous subject's data.
    //
    // It deliberately does NOT fire on the first resolution of a document: that
    // is the return leg of the sign-in round trip, and discarding the pending
    // context there would defeat the whole continuation (F5).
    notifyBriefingContextAuthSubject(next?.sub ?? null)
  }, [])

  useEffect(() => {
    void refreshUser().finally(() => setIsLoading(false))
  }, [refreshUser])

  async function logout() {
    // Clear off-site link trust decisions before navigating away — see
    // OffSiteLinkConfirmModal. Prevents a subsequent user on the same device
    // from inheriting the prior user's remembered domains.
    clearExternalLinkConfirmations()
    // KAN-661 R38. Sign-out is terminal for the briefing continuation. Done here
    // rather than left to the next `/me` because the very next thing this
    // function does is navigate the document away, so there is no next `/me`.
    endBriefingContextSession()
    try {
      const res = await fetch(`${API_BASE_URL}/auth/logout`, {
        method: 'POST',
        credentials: 'include',
      })
      const { logoutUrl } = (await res.json()) as { logoutUrl: string }
      window.location.href = logoutUrl
    } catch {
      window.location.href = '/'
    }
  }

  return (
    <AuthContext.Provider value={{ user, isLoading, isAdmin: user?.isAdmin ?? false, logout , refreshUser}}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth(): AuthContextValue {
  return useContext(AuthContext)
}
